import type { MeasurementSystem, Recipe } from '@recipes/contracts';
import { annotateMeasurements, formatMinutes } from '@recipes/domain';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StarRating,
} from '@recipes/ui';
import {
  ChefHat,
  Clock,
  Copy,
  FolderPlus,
  GitBranch,
  Heart,
  History,
  MoreHorizontal,
  Pencil,
  Share2,
  ShoppingCart,
  Trash2,
  WifiOff,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useNavigationType, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/forms/confirm-dialog';
import { LiveRegion } from '@/components/feedback/live-region';
import { toast } from '@/components/feedback/toaster';
import { PageHeader } from '@/components/layout/page-header';
import { OriginBadge } from '@/components/recipes/origin-badge';
import { OfflineToggle } from '@/features/offline/offline-toggle';
import { AddToCollectionDialog } from '@/features/recipes/add-to-collection-dialog';
import { AddToShoppingDialog } from '@/features/recipes/add-to-shopping-dialog';
import { AddCookingLogDialog, CookingLogList } from '@/features/recipes/cooking-log';
import { RecipeNutritionCard } from '@/features/catalog/recipe-nutrition';
import { InspiredByTasting } from '@/features/tasting/inspired-by';
import { LabelsDisplay } from '@/features/recipes/labels-display';
import { takeLeftPlace } from '@/features/recipes/base-recipes/left-place';
import {
  BackToRecipeBar,
  BaseRecipeBadge,
  LinkedRecipeChip,
  UsedInSection,
} from '@/features/recipes/base-recipes/linked-recipe';
import {
  useDeleteRecipe,
  useDuplicateRecipe,
  useProfile,
  useRecipe,
  useRecipeCookingLog,
  useScalePreview,
  useSetRating,
  useToggleFavorite,
  useUpdateRecipe,
  useVariations,
} from '@/features/recipes/queries';
import { headerTintStyle } from '@/features/appearance/header-tint';
import { RecipePlaceholder } from '@/features/appearance/recipe-placeholder';
import { recipeToWriteInput } from '@/features/recipes/recipe-to-input';
import { ScaledIngredients } from '@/features/recipes/scaled-ingredients';
import { ServingsStepper } from '@/features/recipes/servings-stepper';
import { ShareDialog } from '@/features/recipes/share-dialog';
import { RecipeVideo } from '@/features/recipes/recipe-video';
import { SourceBox } from '@/features/recipes/source-box';
import { isNotFound, isVersionConflict, toastError } from '@/lib/errors';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { usePublicConfig } from '@/lib/hooks/use-public-config';
import { currentFormatLocale, currentLocale } from '@/lib/i18n';
import { useImageSrc } from '@/lib/offline/hooks';
import { OfflineUnavailableError } from '@/lib/offline/hooks';
import { startSyncManager } from '@/lib/offline/queue';
import { NotFoundPage } from '@/components/layout/not-found-page';

function CoverImage({ recipe, offlineCopy }: { recipe: Recipe; offlineCopy: boolean }) {
  const { t } = useTranslation();
  const src = useImageSrc(recipe.cover?.url, recipe.cover?.assetId, 'medium', offlineCopy);
  if (!recipe.cover) {
    return (
      <div className="relative">
        <RecipePlaceholder recipe={recipe} className="aspect-[16/9] w-full rounded-lg" />
        <span className="sr-only">{t('recipes.detail.noImage')}</span>
      </div>
    );
  }
  return (
    <img
      src={src ?? undefined}
      alt={recipe.cover.altText ?? recipe.title}
      className="aspect-[16/9] w-full rounded-lg object-cover"
      width={recipe.cover.width ?? undefined}
      height={recipe.cover.height ?? undefined}
      data-testid="recipe-cover"
    />
  );
}

function GalleryImage({
  media,
  offlineCopy,
  onOpen,
}: {
  media: Recipe['gallery'][number];
  offlineCopy: boolean;
  onOpen: () => void;
}) {
  const src = useImageSrc(media.thumbnailUrl ?? media.url, media.assetId, 'thumb', offlineCopy);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="aspect-square overflow-hidden rounded-md bg-muted"
    >
      <img
        src={src ?? undefined}
        alt={media.altText ?? ''}
        className="size-full object-cover"
        loading="lazy"
      />
    </button>
  );
}

function StepImage({
  step,
  offlineCopy,
}: {
  step: Recipe['stepSections'][number]['steps'][number];
  offlineCopy: boolean;
}) {
  const src = useImageSrc(step.imageUrl, step.imageAssetId, 'medium', offlineCopy);
  if (!step.imageUrl) return null;
  return (
    <img
      src={src ?? undefined}
      alt=""
      className="mt-2 max-h-64 rounded-md object-cover"
      loading="lazy"
    />
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/50 px-3 py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

export function RecipeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();
  const online = useOnlineStatus();
  const query = useRecipe(id);
  const profile = useProfile(online);
  const config = usePublicConfig();
  const recipe = query.data?.recipe;
  const offlineCopy = query.data?.offlineCopy ?? false;

  useEffect(() => startSyncManager(), []);

  const [servings, setServings] = useState<string>('');
  // "Original" or a measurement system, chosen on this page; null = follow the profile.
  const [unitChoice, setUnitChoice] = useState<MeasurementSystem | 'ORIGINAL' | null>(null);
  useEffect(() => {
    if (recipe && servings === '') setServings(recipe.servings);
  }, [recipe, servings]);
  // From a recipe to its base recipe and back the page stays mounted: start over with the new
  // one – at the top, or on the way back where the reader left to follow the link.
  const navigationType = useNavigationType();
  useEffect(() => {
    setServings('');
    setUnitChoice(null);
  }, [id]);
  const shownId = recipe?.id;
  useEffect(() => {
    if (!shownId || shownId !== id) return;
    const leftAt = takeLeftPlace(shownId);
    window.scrollTo({ top: navigationType === 'POP' ? (leftAt ?? 0) : 0, behavior: 'auto' });
  }, [id, shownId, navigationType]);
  // Converted by itself when the recipe was written in another system than the reader's
  // (unless the profile says "show original quantities"); the choice here overrides both.
  const readerSystem = profile.data?.measurementSystem ?? 'METRIC';
  const automatic: MeasurementSystem | 'ORIGINAL' =
    profile.data?.enableUnitConversion && recipe?.unitSystem && recipe.unitSystem !== readerSystem
      ? readerSystem
      : 'ORIGINAL';
  const unitSystemChoice = unitChoice ?? automatic;
  const targetSystem = unitSystemChoice === 'ORIGINAL' ? null : unitSystemChoice;
  const effectiveConvert = targetSystem !== null;
  // Temperatures and lengths in the step text follow the same choice; "Original" leaves the text alone.
  const annotateFor = targetSystem;
  // Empty until the recipe is known, so no preview request fires for a placeholder value.
  const debouncedServings = useDebouncedValue(recipe ? servings || recipe.servings : '', 300);
  const preview = useScalePreview(recipe, debouncedServings, targetSystem);
  const isScaled =
    recipe !== undefined && (debouncedServings !== recipe.servings || effectiveConvert);

  const toggleFavorite = useToggleFavorite();
  const setRating = useSetRating();
  const remove = useDeleteRecipe();
  const duplicate = useDuplicateRecipe();
  const update = useUpdateRecipe();
  const variations = useVariations(recipe?.id, online);
  const cookingLog = useRecipeCookingLog(recipe?.id, online);

  const [shareOpen, setShareOpen] = useState(false);
  const [shoppingOpen, setShoppingOpen] = useState(false);
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [variationOpen, setVariationOpen] = useState(false);
  const [variationLabel, setVariationLabel] = useState('');
  const [lightbox, setLightbox] = useState<Recipe['gallery'][number] | null>(null);

  const steps = useMemo(() => recipe?.stepSections ?? [], [recipe]);
  const totalSteps = steps.reduce((n, s) => n + s.steps.length, 0);

  if (query.isPending) {
    return (
      <div className="mx-auto max-w-4xl space-y-4 p-4 md:p-6" aria-busy="true">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="aspect-[16/9] w-full" />
        <Skeleton className="h-40" />
      </div>
    );
  }
  if (query.isError || !recipe) {
    if (isNotFound(query.error)) return <NotFoundPage />;
    const unavailableOffline = query.error instanceof OfflineUnavailableError;
    return (
      <div className="mx-auto max-w-lg p-6">
        <EmptyState
          icon={unavailableOffline ? <WifiOff /> : undefined}
          title={unavailableOffline ? t('offline.notAvailableTitle') : t('app.unexpectedError')}
          description={
            unavailableOffline
              ? t('offline.notAvailableDescription')
              : t('recipes.detail.loadError')
          }
          action={
            <Button variant="outline" onClick={() => void query.refetch()}>
              {t('app.retry')}
            </Button>
          }
        />
      </div>
    );
  }

  const canEdit = recipe.permissions.canEdit;
  const confirmLabel = (label: Recipe['labels'][number]) => {
    const input = recipeToWriteInput(recipe);
    update.mutate(
      {
        id: recipe.id,
        input: {
          version: recipe.version,
          labels: input.labels.map((l) =>
            l.kind === label.kind && l.value === label.value ? { ...l, confirmed: true } : l,
          ),
          revisionReason: 'Confirm label',
        },
      },
      {
        onSuccess: () => toast.success(t('recipes.labels.confirmed')),
        onError: (error) => {
          if (isVersionConflict(error)) {
            toast.error(t('errors.VERSION_CONFLICT'));
            void query.refetch();
          } else toastError(error);
        },
      },
    );
  };

  const doDuplicate = () =>
    duplicate.mutate(
      { id: recipe.id, body: { asVariation: false } },
      {
        onSuccess: (created) => {
          toast.success(t('recipes.detail.duplicated'));
          navigate(`/recipes/${created.id}`);
        },
        onError: (e) => toastError(e),
      },
    );
  const doVariation = () =>
    duplicate.mutate(
      {
        id: recipe.id,
        body: { asVariation: true, variationLabel: variationLabel.trim() || undefined },
      },
      {
        onSuccess: (created) => {
          setVariationOpen(false);
          toast.success(t('recipes.detail.variationCreated'));
          navigate(`/recipes/${created.id}/edit`);
        },
        onError: (e) => toastError(e),
      },
    );

  const timeItems = [
    recipe.prepTimeMinutes !== null && {
      label: t('recipes.times.prep'),
      value: formatMinutes(recipe.prepTimeMinutes, locale),
    },
    recipe.cookTimeMinutes !== null && {
      label: t('recipes.times.cook'),
      value: formatMinutes(recipe.cookTimeMinutes, locale),
    },
    recipe.restTimeMinutes !== null && {
      label: t('recipes.times.rest'),
      value: formatMinutes(recipe.restTimeMinutes, locale),
    },
    recipe.totalTimeMinutes !== null && {
      label: t('recipes.times.total'),
      value: formatMinutes(recipe.totalTimeMinutes, locale),
    },
  ].filter((x): x is { label: string; value: string } => Boolean(x));

  // Rendered twice (mobile section and desktop column); ids must differ per variant.
  const renderIngredientsPanel = (variant: 'mobile' | 'desktop') => (
    <div
      className="space-y-3"
      data-testid="ingredients-panel"
      data-variant={variant}
      lang={recipe.language ?? undefined}
    >
      <ServingsStepper
        value={servings || recipe.servings}
        onChange={setServings}
        original={recipe.servings}
        label={recipe.servingsLabel}
      />
      <div className="flex items-center gap-2">
        <Label htmlFor={`unit-system-${variant}`} className="shrink-0">
          {t('recipes.detail.units')}
        </Label>
        <Select
          value={unitSystemChoice}
          onValueChange={(v) => setUnitChoice(v as MeasurementSystem | 'ORIGINAL')}
        >
          <SelectTrigger id={`unit-system-${variant}`} data-testid="unit-system" className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ORIGINAL">{t('recipes.detail.unitsOriginal')}</SelectItem>
            <SelectItem value="METRIC">{t('profile.metric')}</SelectItem>
            <SelectItem value="US">{t('profile.us')}</SelectItem>
            <SelectItem value="UK">{t('profile.uk')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {preview.isError && (
        <p className="text-sm text-destructive">{t('recipes.detail.scaleError')}</p>
      )}
      <ScaledIngredients
        recipe={recipe}
        preview={isScaled ? preview.data : undefined}
        loading={isScaled && preview.isFetching}
        linkFrom={recipe.id}
      />
    </div>
  );

  let stepNumber = 0;

  return (
    <div
      className="mx-auto max-w-4xl p-4 md:p-6"
      data-testid="recipe-detail"
      data-recipe-id={recipe.id}
    >
      <LiveRegion />
      <BackToRecipeBar />
      {/* The header takes a soft tint from the photo (appearance spec 6.4). */}
      <div
        className="-mx-4 -mt-4 mb-2 rounded-b-xl px-4 pt-4 md:-mx-6 md:-mt-6 md:px-6 md:pt-6"
        style={headerTintStyle(recipe.cover?.dominantColor)}
        data-testid="recipe-header"
      >
        <PageHeader
          eyebrow={
            <Link to="/recipes" className="underline-offset-4 hover:underline">
              ← {t('recipes.library.title')}
            </Link>
          }
          title={recipe.title}
          description={
            <div className="flex flex-wrap items-center gap-2">
              <OriginBadge origin={recipe.origin} sourceDomain={recipe.source?.domain} />
              {recipe.isBaseRecipe && <BaseRecipeBadge />}
              {recipe.status !== 'READY' && (
                <Badge variant="outline">{t(`recipes.status.${recipe.status}`)}</Badge>
              )}
              {recipe.visibility !== 'PRIVATE' && (
                <Badge variant="outline">{t(`recipes.visibility.${recipe.visibility}`)}</Badge>
              )}
              {recipe.parentRecipeId && (
                <Badge variant="outline">
                  <GitBranch className="mr-1 size-3" aria-hidden />
                  {recipe.variationLabel ?? t('recipes.detail.variation')}
                </Badge>
              )}
              {offlineCopy && (
                <Badge variant="muted" data-testid="offline-copy-badge">
                  <WifiOff className="mr-1 size-3" aria-hidden />
                  {t('offline.badge')}
                </Badge>
              )}
              {recipe.owner && (
                <span className="text-xs">
                  {t('recipes.detail.by', { name: recipe.owner.displayName })}
                </span>
              )}
            </div>
          }
          actions={
            <>
              <Button asChild data-testid="cook-now">
                <Link to={`/cook/${recipe.id}`}>
                  <ChefHat aria-hidden /> {t('recipes.detail.cookNow')}
                </Link>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-pressed={recipe.isFavorite}
                aria-label={
                  recipe.isFavorite ? t('recipes.card.unfavorite') : t('recipes.card.favorite')
                }
                onClick={() =>
                  toggleFavorite.mutate({ id: recipe.id, favorite: !recipe.isFavorite })
                }
                data-testid="detail-favorite"
              >
                <Heart
                  className={recipe.isFavorite ? 'fill-current text-destructive' : ''}
                  aria-hidden
                />
              </Button>
              {canEdit && (
                <Button asChild variant="outline" data-testid="edit-recipe">
                  <Link to={`/recipes/${recipe.id}/edit`}>
                    <Pencil aria-hidden /> {t('common.edit')}
                  </Link>
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label={t('recipes.detail.moreActions')}
                    data-testid="more-actions"
                  >
                    <MoreHorizontal aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setShoppingOpen(true)}>
                    <ShoppingCart aria-hidden /> {t('recipes.detail.addToShopping')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setCollectionOpen(true)}>
                    <FolderPlus aria-hidden /> {t('recipes.detail.addToCollection')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setLogOpen(true)}>
                    <Clock aria-hidden /> {t('recipes.cookingLog.add')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {recipe.permissions.canShare && (
                    <DropdownMenuItem
                      onSelect={() => setShareOpen(true)}
                      data-testid="share-action"
                    >
                      <Share2 aria-hidden /> {t('recipes.detail.share')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onSelect={doDuplicate} disabled={!online}>
                    <Copy aria-hidden /> {t('recipes.detail.duplicate')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setVariationOpen(true)} disabled={!online}>
                    <GitBranch aria-hidden /> {t('recipes.detail.createVariation')}
                  </DropdownMenuItem>
                  {canEdit && (
                    <DropdownMenuItem onSelect={() => navigate(`/recipes/${recipe.id}/history`)}>
                      <History aria-hidden /> {t('recipes.detail.history')}
                    </DropdownMenuItem>
                  )}
                  {recipe.permissions.canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => setDeleteOpen(true)}
                        className="text-destructive"
                        data-testid="delete-action"
                      >
                        <Trash2 aria-hidden /> {t('recipes.detail.moveToTrash')}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          }
        />
      </div>

      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_280px]">
        <div className="space-y-6">
          <CoverImage recipe={recipe} offlineCopy={offlineCopy} />
          {recipe.gallery.length > 0 && (
            <ul
              className="grid grid-cols-4 gap-2 sm:grid-cols-6"
              aria-label={t('recipes.detail.gallery')}
            >
              {recipe.gallery.map((media) => (
                <li key={media.assetId}>
                  <GalleryImage
                    media={media}
                    offlineCopy={offlineCopy}
                    onOpen={() => setLightbox(media)}
                  />
                </li>
              ))}
            </ul>
          )}
          {recipe.description && (
            <p
              className="whitespace-pre-wrap text-base leading-relaxed"
              lang={recipe.language ?? undefined}
            >
              {recipe.description}
            </p>
          )}

          <SourceBox recipe={recipe} />

          {recipe.videoUrl && <RecipeVideo url={recipe.videoUrl} title={recipe.title} />}

          <LabelsDisplay
            recipe={recipe}
            onConfirmLabel={canEdit ? confirmLabel : undefined}
            confirming={update.isPending}
            onTagsChanged={canEdit && online ? () => void query.refetch() : undefined}
          />

          <section aria-labelledby="ingredients-heading" className="space-y-3 md:hidden">
            <h2 id="ingredients-heading" className="text-xl font-semibold">
              {t('recipes.detail.ingredients')}
            </h2>
            {renderIngredientsPanel('mobile')}
          </section>

          <section aria-labelledby="steps-heading" className="space-y-4">
            <h2 id="steps-heading" className="text-xl font-semibold">
              {t('recipes.detail.steps')}{' '}
              <span className="text-base font-normal text-muted-foreground">({totalSteps})</span>
            </h2>
            {totalSteps === 0 && (
              <p className="text-sm text-muted-foreground">{t('recipes.detail.noSteps')}</p>
            )}
            {steps.map((section) => (
              <div key={section.id}>
                {section.title && <h3 className="mb-2 font-semibold">{section.title}</h3>}
                <ol className="space-y-4" lang={recipe.language ?? undefined}>
                  {section.steps.map((step) => {
                    stepNumber += 1;
                    return (
                      <li key={step.id} className="flex gap-3" data-testid="step-item">
                        <span
                          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground"
                          aria-hidden
                        >
                          {stepNumber}
                        </span>
                        <div className="min-w-0 flex-1">
                          <span className="sr-only">
                            {t('recipes.detail.stepN', { n: stepNumber })}
                          </span>
                          <p className="whitespace-pre-wrap leading-relaxed">
                            {annotateFor
                              ? annotateMeasurements(step.instruction, annotateFor, currentLocale())
                              : step.instruction}
                          </p>
                          {step.durationSeconds ? (
                            <p className="mt-1 inline-flex items-center gap-1 text-sm text-muted-foreground">
                              <Clock className="size-3.5" aria-hidden />{' '}
                              {formatMinutes(Math.round(step.durationSeconds / 60), locale)}
                            </p>
                          ) : null}
                          {(step.linkedRecipes ?? []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="step-links">
                              {step.linkedRecipes!.map((link) => (
                                <LinkedRecipeChip
                                  key={link.id}
                                  link={link}
                                  fromRecipeId={recipe.id}
                                />
                              ))}
                            </div>
                          )}
                          <StepImage step={step} offlineCopy={offlineCopy} />
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}
          </section>

          {recipe.notes && (
            <section aria-labelledby="notes-heading">
              <h2 id="notes-heading" className="mb-2 text-xl font-semibold">
                {t('recipes.detail.notes')}
              </h2>
              <p className="whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-sm">
                {recipe.notes}
              </p>
            </section>
          )}

          <UsedInSection recipe={recipe} />

          <InspiredByTasting recipeId={recipe.id} enabled={online} />

          <RecipeNutritionCard recipeId={recipe.id} enabled={online} />

          <section aria-labelledby="log-heading" className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 id="log-heading" className="text-xl font-semibold">
                {t('recipes.cookingLog.title')}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLogOpen(true)}
                data-testid="add-cooking-log"
              >
                {t('recipes.cookingLog.add')}
              </Button>
            </div>
            {recipe.cookCount > 0 && (
              <p className="text-sm text-muted-foreground">
                {t('recipes.cookingLog.summary', { count: recipe.cookCount })}
                {recipe.lastCookedAt &&
                  ` · ${t('recipes.cookingLog.last', { date: new Date(recipe.lastCookedAt).toLocaleDateString(currentFormatLocale()) })}`}
              </p>
            )}
            {cookingLog.data ? (
              <CookingLogList entries={cookingLog.data.items} />
            ) : !online ? (
              <p className="text-sm text-muted-foreground">{t('offline.sectionUnavailable')}</p>
            ) : null}
          </section>

          {variations.data && variations.data.length > 0 && (
            <section aria-labelledby="variations-heading">
              <h2 id="variations-heading" className="mb-2 text-xl font-semibold">
                {t('recipes.detail.variations')}
              </h2>
              <ul className="divide-y rounded-lg border">
                {variations.data.map((v) => (
                  <li key={v.id}>
                    <Link
                      to={`/recipes/${v.id}`}
                      className="flex items-center justify-between p-3 hover:bg-accent"
                    >
                      <span>{v.title}</span>
                      {v.variationLabel && <Badge variant="outline">{v.variationLabel}</Badge>}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <aside className="space-y-4 md:sticky md:top-4 md:self-start">
          <dl className="grid grid-cols-2 gap-2">
            <MetaItem
              label={t('common.servings')}
              value={`${recipe.servings} ${recipe.servingsLabel ?? ''}`.trim()}
            />
            {recipe.difficulty && (
              <MetaItem
                label={t('recipes.filters.difficulty')}
                value={t(`recipes.difficulty.${recipe.difficulty}`)}
              />
            )}
            {timeItems.map((item) => (
              <MetaItem key={item.label} label={item.label} value={item.value} />
            ))}
          </dl>
          <div>
            <p className="mb-1 text-sm font-medium">{t('recipes.detail.yourRating')}</p>
            <StarRating
              value={recipe.rating}
              onChange={(stars) => setRating.mutate({ id: recipe.id, stars })}
              label={t('recipes.detail.yourRating')}
            />
          </div>
          <OfflineToggle recipe={recipe} className="w-full" />
          <div className="hidden space-y-3 md:block">
            <h2 className="text-lg font-semibold">{t('recipes.detail.ingredients')}</h2>
            {renderIngredientsPanel('desktop')}
          </div>
        </aside>
      </div>

      <ShareDialog
        recipeId={recipe.id}
        open={shareOpen}
        onOpenChange={setShareOpen}
        publicSharingEnabled={config.data?.features.public_sharing ?? true}
      />
      <AddToShoppingDialog
        recipeId={recipe.id}
        defaultServings={servings || recipe.servings}
        servingsLabel={recipe.servingsLabel}
        open={shoppingOpen}
        onOpenChange={setShoppingOpen}
      />
      <AddToCollectionDialog
        recipeId={recipe.id}
        open={collectionOpen}
        onOpenChange={setCollectionOpen}
      />
      <AddCookingLogDialog
        recipeId={recipe.id}
        open={logOpen}
        onOpenChange={setLogOpen}
        defaultServings={servings || recipe.servings}
        servingsLabel={recipe.servingsLabel}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('recipes.detail.deleteTitle')}
        description={t('recipes.detail.deleteDescription')}
        confirmLabel={t('recipes.detail.moveToTrash')}
        destructive
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(recipe.id, {
            onSuccess: () => {
              toast.success(t('recipes.detail.deleted'));
              navigate('/recipes');
            },
            onError: (e) => toastError(e),
          })
        }
      />
      <Dialog open={variationOpen} onOpenChange={setVariationOpen}>
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('recipes.detail.createVariation')}</DialogTitle>
            <DialogDescription>{t('recipes.detail.variationDescription')}</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="variation-label" className="mb-1 block">
              {t('recipes.detail.variationLabel')}
            </Label>
            <Input
              id="variation-label"
              value={variationLabel}
              onChange={(e) => setVariationLabel(e.target.value)}
              maxLength={80}
              placeholder={t('recipes.detail.variationPlaceholder')}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVariationOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={doVariation} loading={duplicate.isPending}>
              {t('recipes.detail.createVariation')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={lightbox !== null} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent closeLabel={t('common.close')} className="max-w-3xl p-2">
          {lightbox && (
            <>
              <DialogTitle className="sr-only">
                {lightbox.altText ?? t('recipes.detail.gallery')}
              </DialogTitle>
              <img
                src={lightbox.url}
                alt={lightbox.altText ?? ''}
                className="max-h-[80vh] w-full rounded object-contain"
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
